import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, CircularProgress, Collapse, List, ListItem, Paper, Typography} from '@mui/material';
import moment from 'moment-timezone';
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {i18n} from 'src/i18n';
import GestorDocumentalService from 'src/modules/gestorDocumental/gestorDocumentalService';
import layoutActions from 'src/modules/layout/layoutActions';
import DrawerButton from 'src/view/shared/view/DrawerButton';
import GestorDocumentalFormPage from '../form/GestorDocumentalFormPage';

interface DocumentoNodo {
  id: string;
  nombre: string;
  tipoDocumento: string;
  version?: string;
  validoHasta?: string;
  isExpanded: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  children: DocumentoNodo[];
  documentoPrincipal?: {
    id: string;
  };
  nivel: number;
}

interface ArbolState {
  nodos: DocumentoNodo[];
  loading: boolean;
  error: string | null;
  visitados: Set<string>;
  selectedDocumentId: string | null;
}

const DocumentosArbolView: React.FC<{documentoRaizId?: string}> = ({documentoRaizId}) => {
  const dispatch = useDispatch();
  const [arbolState, setArbolState] = useState<ArbolState>({
    nodos: [],
    loading: false,
    error: null,
    visitados: new Set<string>(),
    selectedDocumentId: documentoRaizId || null,
  });
  const [registroParaEditar, setRegistroParaEditar] = useState<any | null>(null);

  // Función para cargar documentos raíz (los que no tienen documentoPrincipal)
  const cargarDocumentosRaiz = useCallback(async () => {
    try {
      setArbolState((prev) => ({...prev, loading: true, error: null}));

      // Si tenemos un documentoRaizId, cargamos solo ese documento
      if (documentoRaizId) {
        const documento = await GestorDocumentalService.find(documentoRaizId);
        console.log('Documento raíz: uuu', documento);
        if (documento) {
          const nodoRaiz: DocumentoNodo = {
            id: documento.id,
            nombre: documento.nombre,
            tipoDocumento: documento.tipoDocumento,
            version: documento.version,
            validoHasta: documento.validoHasta,
            isExpanded: false,
            isLoaded: false,
            isLoading: false,
            children: [],
            documentoPrincipal: documento.documentoPrincipal,
            nivel: 0,
          };

          const visitados = new Set<string>([documento.id]);

          setArbolState({
            nodos: [nodoRaiz],
            loading: false,
            error: null,
            visitados,
            selectedDocumentId: documento.id,
          });
        }
      } else {
        // Si no tenemos un documentoRaizId, cargamos todos los documentos raíz
        const response = await GestorDocumentalService.list({documentoPrincipal: null}, 'createdAt', 100, 0);

        console.log('Response de documentos raíz  xsxs:', response);

        const documentosRaiz = response.rows?.filter((doc) => !doc.documentoPrincipal) || [];

        const nodosRaiz = documentosRaiz.map((doc) => ({
          ...doc,
          isExpanded: false,
          isLoaded: false,
          isLoading: false,
          children: [],
          nivel: 0,
        }));

        console.log('Documentos raíz:', nodosRaiz);

        const visitados = new Set<string>(documentosRaiz.map((doc) => doc.id));

        setArbolState({
          nodos: nodosRaiz,
          loading: false,
          error: null,
          visitados,
          selectedDocumentId: null,
        });
      }
    } catch (error) {
      console.error('Error al cargar los documentos raíz:', error);
      setArbolState((prev) => ({
        ...prev,
        loading: false,
        error: 'Error al cargar los documentos. Por favor, intente de nuevo.',
      }));
    }
  }, [documentoRaizId]);

  // Cargar documentos hijos para un nodo específico
  const cargarHijos = async (documentoId: string, nivel: number) => {
    try {
      // Actualizar estado para mostrar carga
      setArbolState((prevState) => {
        const actualizarNodoRecursivo = (nodos: DocumentoNodo[]): DocumentoNodo[] => {
          return nodos.map((nodo) => {
            if (nodo.id === documentoId) {
              return {...nodo, isLoading: true};
            } else if (nodo.children.length > 0) {
              return {...nodo, children: actualizarNodoRecursivo(nodo.children)};
            }
            return nodo;
          });
        };

        return {
          ...prevState,
          nodos: actualizarNodoRecursivo(prevState.nodos),
        };
      });

      // Obtener documentos hijos
      const response = await GestorDocumentalService.list(
        {
          documentoPrincipal: documentoId,
        },
        'createdAt',
        100,
        0,
      );

      console.log('Response de documentos hijos:', response);

      const documentosHijos = response.rows || [];

      // Filtrar documentos ya visitados para evitar ciclos
      const hijosNoVisitados = documentosHijos.filter((doc) => !arbolState.visitados.has(doc.id));

      // Crear nodos hijos
      const nodosHijos = documentosHijos.map((doc) => ({
        ...doc,
        isExpanded: false,
        isLoaded: false,
        isLoading: false,
        children: [],
        documentoPrincipal: {id: documentoId},
        nivel: nivel + 1,
      }));

      // Actualizar estado con los nodos hijos y marcar el nodo como cargado y expandido
      setArbolState((prevState) => {
        // Añadir nuevos documentos a visitados
        const nuevosVisitados = new Set(prevState.visitados);
        hijosNoVisitados.forEach((doc) => nuevosVisitados.add(doc.id));

        const actualizarNodoRecursivo = (nodos: DocumentoNodo[]): DocumentoNodo[] => {
          return nodos.map((nodo) => {
            if (nodo.id === documentoId) {
              return {
                ...nodo,
                isLoading: false,
                isLoaded: true,
                isExpanded: true,
                children: nodosHijos,
              };
            } else if (nodo.children.length > 0) {
              return {...nodo, children: actualizarNodoRecursivo(nodo.children)};
            }
            return nodo;
          });
        };

        return {
          ...prevState,
          nodos: actualizarNodoRecursivo(prevState.nodos),
          visitados: nuevosVisitados,
        };
      });
    } catch (error) {
      console.error(`Error al cargar los hijos para el documento ${documentoId}:`, error);

      // Actualizar estado para mostrar error
      setArbolState((prevState) => {
        const actualizarNodoRecursivo = (nodos: DocumentoNodo[]): DocumentoNodo[] => {
          return nodos.map((nodo) => {
            if (nodo.id === documentoId) {
              return {...nodo, isLoading: false};
            } else if (nodo.children.length > 0) {
              return {...nodo, children: actualizarNodoRecursivo(nodo.children)};
            }
            return nodo;
          });
        };

        return {
          ...prevState,
          nodos: actualizarNodoRecursivo(prevState.nodos),
          error: `Error al cargar los documentos hijos para ${documentoId}.`,
        };
      });
    }
  };

  const cargarDocumentoParaEditar = async (id: string) => {
    try {
      const doc = await GestorDocumentalService.find(id);
      console.log('Documento para editar:', doc);
      setRegistroParaEditar(doc);

      dispatch(
        layoutActions.doVisibleDrawer({
          drawerId: 'DrawerGestorDocumentalFormPage',
          drawerVisible: true,
          drawerChanged: moment().unix(),
          recordId: id,
          drawerUpdateComponent: <GestorDocumentalFormPage record={doc} drawer={true} disabled={false} />,
        }),
      );
    } catch (error) {
      console.error('Error cargando documento para edición:', error);
    }
  };

  // Manejar clic en un nodo para expandir/contraer
  const handleNodeClick = (documentoId: string, isLoaded: boolean, nivel: number) => {
    setArbolState((prevState) => {
      const actualizarNodoRecursivo = (nodos: DocumentoNodo[]): DocumentoNodo[] => {
        return nodos.map((nodo) => {
          if (nodo.id === documentoId) {
            // Si el nodo ya está cargado, solo toggle el estado expandido
            if (isLoaded) {
              return {...nodo, isExpanded: !nodo.isExpanded};
            }
            // Si no está cargado, simplemente devolver el nodo
            return nodo;
          } else if (nodo.children.length > 0) {
            return {...nodo, children: actualizarNodoRecursivo(nodo.children)};
          }
          return nodo;
        });
      };

      return {
        ...prevState,
        nodos: actualizarNodoRecursivo(prevState.nodos),
        selectedDocumentId: documentoId,
      };
    });

    // Si el nodo no está cargado, cargar sus hijos
    if (!isLoaded) {
      cargarHijos(documentoId, nivel);
    }
  };

  // Componente recursivo para renderizar un nodo y sus hijos
  const DocumentoNodoComponente: React.FC<{nodo: DocumentoNodo}> = ({nodo}) => {
    const getIndentation = (nivel: number) => `${nivel * 16}px`;

    return (
      <>
        <ListItem disableGutters sx={{pl: getIndentation(nodo.nivel)}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              padding: '5px',
              ':hover': {
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
              },
            }}
            onClick={() => handleNodeClick(nodo.id, nodo.isLoaded, nodo.nivel)}>
            <Box display={'flex'}>
              <DescriptionIcon fontSize="small" color="primary" />
              <Box>
                <Typography sx={{fontWeight: arbolState.selectedDocumentId === nodo.id ? 'bold' : 'normal'}}>
                  {nodo.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {nodo.tipoDocumento} {nodo.version ? `- ${nodo.version}` : ''}
                </Typography>
                {nodo.validoHasta && (
                  <Typography variant="body2" color="text.secondary">
                    Válido hasta: {moment(nodo.validoHasta).format('DD/MM/YYYY')}
                  </Typography>
                )}
              </Box>
              <Box marginLeft={'10px'} sx={{minWidth: '24px'}}>
                {nodo.isLoading ? (
                  <CircularProgress size={18} />
                ) : nodo.children.length > 0 ? (
                  nodo.isExpanded ? (
                    <ExpandMoreIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                ) : null}
              </Box>
            </Box>

            <Box>
              <Box onClick={(e) => e.stopPropagation()} sx={{ml: 2}}>
                <DrawerButton
                  drawerId="DrawerGestorDocumentalFormPage"
                  type="icon"
                  tooltipTitle={i18n('common.edit')}
                  buttonTitle={i18n('common.edit')}
                  buttonIcon={<EditIcon />}
                  width={window.innerWidth * 0.55}
                  component={<GestorDocumentalFormPage record={nodo} disabled={false} drawer={true} />}
                />
              </Box>
            </Box>
          </Box>
        </ListItem>

        {nodo.children.length > 0 && (
          <Collapse in={nodo.isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {nodo.children.map((childNode) => (
                <DocumentoNodoComponente key={childNode.id} nodo={childNode} />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  // Cargar documentos raíz al montar el componente
  useEffect(() => {
    cargarDocumentosRaiz();
  }, [cargarDocumentosRaiz]);

  return (
    <Paper sx={{p: 2, mb: 2}}>
      <Typography variant="h6" gutterBottom>
        {i18n('entities.gestorDocumental.fields.jerarquiaDocumentos')}
      </Typography>

      {arbolState.loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : arbolState.error ? (
        <Typography color="error">{arbolState.error}</Typography>
      ) : arbolState.nodos.length === 0 ? (
        <Typography color="textSecondary" sx={{p: 2}}>
          {i18n('entities.gestorDocumental.view.noDocumentos')}
        </Typography>
      ) : (
        <List>
          {arbolState.nodos.map((nodo) => (
            <DocumentoNodoComponente key={nodo.id} nodo={nodo} />
          ))}
        </List>
      )}
    </Paper>
  );
};

export default DocumentosArbolView;
